import {Key, useRef, useEffect, MouseEvent} from 'react'

interface UseShiftSelectProps<TKey extends Key> {
    selectedKeys: TKey[];
    onSelectionChange: (keys: TKey[]) => void;
    getKeyAtIndex: (displayIndex: number) => TKey | undefined;
    getKeysInRange: (start: number, end: number) => TKey[];
    /** When this value changes, the anchor is reset. Use to reset anchor when sort changes. */
    resetKey?: string | number | null;
}

/**
 * Hook for Gmail-style shift-select behavior.
 * - Click: toggles selection and sets anchor
 * - Shift+click: applies the same action (select/deselect) to the range
 */
export const useShiftSelect = <TKey extends Key>(props: UseShiftSelectProps<TKey>) => {
    const {selectedKeys, onSelectionChange, getKeyAtIndex, getKeysInRange, resetKey} = props;

    const anchorDisplayIndex = useRef<number | null>(null);
    const anchorActionWasSelect = useRef<boolean>(true);

    // Reset anchor when sort/filter changes
    useEffect(() => {
        anchorDisplayIndex.current = null;
    }, [resetKey]);

    const handleClick = (e: MouseEvent, displayIndex: number) => {
        // Prevent text selection when shift-clicking
        if (e.shiftKey) {
            e.preventDefault();
        }

        const clickedKey = getKeyAtIndex(displayIndex);
        if (clickedKey === undefined) return;

        if (e.shiftKey && anchorDisplayIndex.current !== null) {
            // Shift-click: apply anchor action to range
            const start = Math.min(anchorDisplayIndex.current, displayIndex);
            const end = Math.max(anchorDisplayIndex.current, displayIndex);
            const keysInRange = getKeysInRange(start, end);

            if (anchorActionWasSelect.current) {
                // Add all keys in range to selection
                const newKeys = [...selectedKeys];
                keysInRange.forEach(key => {
                    if (!newKeys.includes(key)) {
                        newKeys.push(key);
                    }
                });
                onSelectionChange(newKeys);
            } else {
                // Remove all keys in range from selection
                onSelectionChange(selectedKeys.filter(k => !keysInRange.includes(k)));
            }
        } else {
            // Normal click: toggle selection, set anchor and remember action
            anchorDisplayIndex.current = displayIndex;
            const wasSelected = selectedKeys.includes(clickedKey);
            anchorActionWasSelect.current = !wasSelected;

            if (wasSelected) {
                onSelectionChange(selectedKeys.filter(k => k !== clickedKey));
            } else {
                onSelectionChange([...selectedKeys, clickedKey]);
            }
        }
    };

    return handleClick;
};
